import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '#app/utils/misc.tsx'

const buttonVariants = cva(
	'ring-ring ring-offset-background inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium active:scale-[0.98] ring-offset-2 outline-hidden transition-colors cursor-pointer focus-within:ring-2 focus-visible:ring-2 disabled:pointer-events-none disabled:opacity-50',
	{
		variants: {
			variant: {
				default: 'bg-primary text-primary-foreground hover:bg-primary/85',
				destructive:
					'bg-destructive text-destructive-foreground hover:bg-destructive/80',
				outline:
					'border-input bg-background hover:bg-accent hover:text-accent-foreground border',
				secondary:
					'bg-secondary text-secondary-foreground hover:bg-secondary/80',
				ghost: 'hover:bg-accent hover:text-accent-foreground',
				link: 'text-brand underline-offset-4 hover:underline',
			},
			size: {
				default: 'h-11 px-4 py-2',
				wide: 'px-24 py-5',
				sm: 'h-10 rounded-md px-3',
				lg: 'h-12 rounded-md px-8',
				pill: 'px-12 py-3 leading-3',
				icon: 'size-11',
			},
		},
		defaultVariants: {
			variant: 'default',
			size: 'default',
		},
	},
)

export type ButtonVariant = VariantProps<typeof buttonVariants>

const Button = ({
	className,
	variant,
	size,
	asChild = false,
	...props
}: React.ComponentProps<'button'> &
	ButtonVariant & {
		asChild?: boolean
	}) => {
	const Comp = asChild ? Slot : 'button'
	return (
		<Comp
			data-slot="button"
			className={cn(buttonVariants({ variant, size, className }))}
			{...props}
		/>
	)
}

export { Button, buttonVariants }
